import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';

interface Service {
  id: string;
  title: string;
  tagline: string;
  description: string;
  image: string;
  icon: string;
  features: string[];
  badge?: string;
  badgeColor?: string;
  deliveryTime: string;
  coverage: string;
}

const SERVICES: Service[] = [
  {
    id: 'air-freight',
    title: 'Air Freight',
    tagline: 'Speed Without Compromise',
    description: 'Premium air cargo solutions connecting over 180 countries. From temperature-sensitive pharmaceuticals to oversized industrial machinery, our air freight network guarantees reliability at altitude.',
    image: 'https://readdy.ai/api/search-image?query=dramatic%20cargo%20airplane%20on%20tarmac%20at%20dusk%20with%20orange%20sunset%20sky%2C%20ground%20crew%20loading%20heavy%20freight%20containers%2C%20professional%20aviation%20logistics%20facility%20with%20bright%20runway%20lights%2C%20wide%20angle%20shot%2C%20ultra-realistic%2C%20cinematic%20quality%2C%20deep%20shadows%20and%20warm%20tones&width=800&height=500&seq=srv-air-001&orientation=landscape',
    icon: 'ri-flight-takeoff-line',
    features: ['Door-to-door service worldwide', 'Real-time cargo tracking', 'Hazardous materials certified', 'Charter flights available', 'Customs clearance support'],
    badge: 'Most Popular',
    badgeColor: 'bg-amber-400 text-amber-900',
    deliveryTime: '1–3 Days',
    coverage: '180+ Countries',
  },
  {
    id: 'warehousing',
    title: 'Warehousing',
    tagline: 'Intelligent Storage Solutions',
    description: 'State-of-the-art fulfillment centers with climate control, automated sorting and 24/7 security. We manage inventory so you can focus on growing your business, not storing it.',
    image: 'https://readdy.ai/api/search-image?query=vast%20modern%20automated%20warehouse%20interior%20with%20towering%20steel%20storage%20racks%20stretching%20to%20ceiling%2C%20industrial%20forklifts%20and%20conveyor%20belts%2C%20workers%20in%20orange%20safety%20vests%2C%20bright%20overhead%20LED%20lighting%2C%20clean%20concrete%20floors%2C%20professional%20logistics%20center%20atmosphere%2C%20ultra-realistic%20photography&width=800&height=500&seq=srv-ware-002&orientation=landscape',
    icon: 'ri-store-3-line',
    features: ['Climate-controlled zones', 'Inventory management system', '24/7 CCTV security', 'Cross-docking capability', 'Pick & pack services'],
    deliveryTime: 'On-Demand',
    coverage: '50+ Facilities',
  },
  {
    id: 'ocean-cargo',
    title: 'Ocean Cargo',
    tagline: 'Boundless Freight by Sea',
    description: 'Full container load (FCL) and less-than-container load (LCL) services across all major global shipping lanes. Cost-effective bulk freight solutions for businesses that move the world.',
    image: 'https://readdy.ai/api/search-image?query=massive%20modern%20container%20ship%20sailing%20on%20calm%20ocean%20at%20golden%20sunrise%2C%20colorful%20shipping%20containers%20stacked%20high%2C%20distant%20port%20skyline%2C%20breathtaking%20wide%20angle%20maritime%20photography%2C%20dramatic%20sky%20with%20orange%20clouds%2C%20ultra-sharp%20realistic%20detail&width=800&height=500&seq=srv-ocean-003&orientation=landscape',
    icon: 'ri-ship-line',
    features: ['FCL & LCL options', 'Port-to-port & door-to-door', 'Reefer containers available', 'Bill of lading management', 'Marine insurance included'],
    deliveryTime: '14–45 Days',
    coverage: 'Global Ports',
  },
  {
    id: 'road-logistics',
    title: 'Road Logistics',
    tagline: 'Your Network, On Wheels',
    description: 'Comprehensive ground transportation across the continent with a modern fleet of refrigerated, flatbed and standard vehicles. Full truckload and partial load options with live GPS tracking.',
    image: 'https://readdy.ai/api/search-image?query=fleet%20of%20professional%20white%20logistics%20trucks%20driving%20on%20wide%20highway%20at%20dawn%2C%20golden%20hour%20light%2C%20open%20road%20stretching%20into%20horizon%2C%20misty%20mountains%20in%20background%2C%20cinematic%20trucking%20photography%2C%20ultra-realistic%2C%20clean%20modern%20vehicles%20with%20company%20branding&width=800&height=500&seq=srv-road-004&orientation=landscape',
    icon: 'ri-truck-line',
    features: ['FTL & LTL shipping', 'Live GPS fleet tracking', 'Refrigerated transport', 'Hazardous goods certified', 'Last-mile delivery network'],
    deliveryTime: '1–7 Days',
    coverage: 'Continental',
  },
  {
    id: 'express',
    title: 'Express Delivery',
    tagline: 'When Hours Matter Most',
    description: 'Guaranteed next-day and same-business-day delivery for urgent documents, parcels and freight. Our dedicated express network prioritises your time-critical shipments above all else.',
    image: 'https://readdy.ai/api/search-image?query=professional%20courier%20on%20sleek%20motorcycle%20speeding%20through%20modern%20city%20street%20at%20dusk%2C%20motion%20blur%20on%20background%20buildings%2C%20delivery%20bag%20on%20back%2C%20dynamic%20urban%20photography%2C%20neon%20lights%20reflecting%20on%20wet%20pavement%2C%20cinematic%20fast%20motion%20composition%2C%20ultra-realistic&width=800&height=500&seq=srv-express-005&orientation=landscape',
    icon: 'ri-rocket-2-line',
    features: ['Next-day guaranteed', 'Time-definite windows', 'Priority handling', 'Real-time push notifications', 'Signature on delivery'],
    badge: 'Fast',
    badgeColor: 'bg-red-500 text-white',
    deliveryTime: 'Next Day',
    coverage: 'Nationwide',
  },
  {
    id: 'same-day',
    title: 'Same Day',
    tagline: 'From Us to You, Today',
    description: 'Ultra-fast same-day collection and delivery within metro and extended city zones. Ideal for e-commerce, healthcare, legal and financial sectors where immediate delivery is non-negotiable.',
    image: 'https://readdy.ai/api/search-image?query=friendly%20professional%20delivery%20person%20in%20uniform%20smiling%20while%20handing%20small%20brown%20cardboard%20package%20to%20customer%20at%20bright%20modern%20doorstep%2C%20warm%20afternoon%20sunlight%2C%20clean%20residential%20neighbourhood%2C%20photorealistic%2C%20welcoming%20and%20professional%20atmosphere&width=800&height=500&seq=srv-sameday-006&orientation=landscape',
    icon: 'ri-flashlight-line',
    features: ['2–4 hour delivery window', 'Live driver tracking', 'Photo proof of delivery', 'Available 7 days/week', 'Fragile item specialist'],
    badge: 'Fastest',
    badgeColor: 'bg-green-500 text-white',
    deliveryTime: 'Same Day',
    coverage: 'Metro Areas',
  },
  {
    id: 'standard',
    title: 'Standard Delivery',
    tagline: 'Reliable. Affordable. Always.',
    description: 'Our core delivery network designed for businesses and individuals who want dependable service without premium pricing. Fully tracked parcels with flexible scheduling and secure handling.',
    image: 'https://readdy.ai/api/search-image?query=modern%20logistics%20sorting%20facility%20with%20hundreds%20of%20parcels%20on%20automated%20conveyor%20belts%2C%20workers%20scanning%20packages%2C%20bright%20industrial%20lighting%2C%20organized%20systematic%20operations%2C%20professional%20warehouse%20environment%2C%20aerial%20side%20view%2C%20ultra-realistic%20photography&width=800&height=500&seq=srv-standard-007&orientation=landscape',
    icon: 'ri-box-3-line',
    features: ['2–5 day delivery', 'Full parcel tracking', 'Flexible pickup times', 'Signature options', 'Return label included'],
    deliveryTime: '2–5 Days',
    coverage: 'Nationwide',
  },
  {
    id: 'late-night',
    title: 'Late Night Delivery',
    tagline: 'The City Never Sleeps',
    description: 'Dedicated overnight delivery windows from 8 PM to 4 AM for businesses requiring off-hours logistics. Zero daytime disruption, maximum security and discreet handling for sensitive cargo.',
    image: 'https://readdy.ai/api/search-image?query=lone%20white%20delivery%20truck%20driving%20on%20dark%20empty%20highway%20at%20night%2C%20dramatic%20light%20trails%20from%20headlights%2C%20city%20skyline%20glowing%20in%20distant%20background%2C%20stars%20visible%20in%20sky%2C%20long%20exposure%20photography%20effect%2C%20moody%20dark%20blue%20and%20amber%20colour%20palette%2C%20cinematic%20composition%2C%20ultra-realistic&width=800&height=500&seq=srv-night-008&orientation=landscape',
    icon: 'ri-moon-line',
    features: ['8 PM – 4 AM windows', 'Dedicated night fleet', 'Restricted-area access', 'Discreet & secure handling', 'Ideal for restaurants & retail'],
    badge: 'Unique',
    badgeColor: 'bg-indigo-500 text-white',
    deliveryTime: '8 PM–4 AM',
    coverage: 'Metro Areas',
  },
];

const STATS = [
  { value: '180+', label: 'Countries Served' },
  { value: '2.4M', label: 'Deliveries/Month' },
  { value: '99.7%', label: 'On-Time Rate' },
  { value: '24/7', label: 'Operations' },
];

function useInView(threshold = 0.15) {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) { setVisible(true); obs.disconnect(); } }, { threshold });
    obs.observe(el);
    return () => obs.disconnect();
  }, [threshold]);
  return { ref, visible };
}

function ServiceCard({ service, reverse }: { service: Service; reverse?: boolean }) {
  const { ref, visible } = useInView();
  return (
    <div
      ref={ref}
      id={service.id}
      className={`grid grid-cols-1 lg:grid-cols-2 gap-0 rounded-3xl overflow-hidden border border-gray-100 bg-white transition-all duration-700 ${
        visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
      }`}
    >
      {/* Image */}
      <div className={`relative h-72 lg:h-auto min-h-72 ${reverse ? 'lg:order-2' : ''}`}>
        <img
          src={service.image}
          alt={service.title}
          className="w-full h-full object-cover object-center"
          loading="lazy"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent" />
        {service.badge && (
          <div className={`absolute top-5 left-5 px-3 py-1 rounded-full text-xs font-bold ${service.badgeColor}`}>
            {service.badge}
          </div>
        )}
        <div className="absolute bottom-5 left-5 flex gap-3">
          <div className="bg-black/40 backdrop-blur-sm border border-white/20 rounded-xl px-3 py-2 text-white text-xs">
            <div className="text-white/60 text-xs mb-0.5">Delivery</div>
            <div className="font-semibold">{service.deliveryTime}</div>
          </div>
          <div className="bg-black/40 backdrop-blur-sm border border-white/20 rounded-xl px-3 py-2 text-white text-xs">
            <div className="text-white/60 text-xs mb-0.5">Coverage</div>
            <div className="font-semibold">{service.coverage}</div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className={`flex flex-col justify-center p-10 ${reverse ? 'lg:order-1' : ''}`}>
        <div
          className="w-12 h-12 rounded-2xl flex items-center justify-center mb-5"
          style={{ background: 'var(--primary-light)' }}
        >
          <i className={`${service.icon} text-2xl`} style={{ color: 'var(--primary)' }} />
        </div>
        <div className="text-xs font-semibold uppercase tracking-widest mb-2" style={{ color: 'var(--primary)' }}>
          {service.tagline}
        </div>
        <h2 className="font-heading font-bold text-3xl text-gray-900 mb-4">{service.title}</h2>
        <p className="text-gray-500 text-sm leading-relaxed mb-6">{service.description}</p>
        <ul className="space-y-2.5 mb-8">
          {service.features.map((f) => (
            <li key={f} className="flex items-center gap-3 text-sm text-gray-700">
              <div
                className="w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0"
                style={{ background: 'var(--primary-light)' }}
              >
                <i className="ri-check-line text-xs" style={{ color: 'var(--primary)' }} />
              </div>
              {f}
            </li>
          ))}
        </ul>
        <div className="flex items-center gap-3">
          <Link
            to="/contact"
            className="inline-flex items-center gap-2 px-6 py-3 rounded-xl text-sm font-semibold text-white whitespace-nowrap transition-opacity hover:opacity-90 cursor-pointer"
            style={{ background: 'var(--primary)' }}
          >
            Get a Quote
            <i className="ri-arrow-right-line" />
          </Link>
          <Link
            to="/track"
            className="inline-flex items-center gap-2 px-6 py-3 rounded-xl text-sm font-semibold text-gray-700 border border-gray-200 hover:border-gray-300 whitespace-nowrap transition-colors cursor-pointer"
          >
            Track Shipment
          </Link>
        </div>
      </div>
    </div>
  );
}

export default function ServicesPage() {
  const heroRef = useRef<HTMLDivElement>(null);
  const [scrollY, setScrollY] = useState(0);
  useEffect(() => {
    const handler = () => setScrollY(window.scrollY);
    window.addEventListener('scroll', handler, { passive: true });
    return () => window.removeEventListener('scroll', handler);
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Hero */}
      <div
        ref={heroRef}
        className="relative min-h-screen flex items-center justify-center overflow-hidden"
        style={{ background: 'linear-gradient(135deg, #071a1a 0%, #0d2b2b 40%, #0a4040 100%)' }}
      >
        {/* Background image */}
        <div
          className="absolute inset-0 bg-cover bg-center opacity-25"
          style={{
            backgroundImage: `url(https://readdy.ai/api/search-image?query=global%20logistics%20network%20aerial%20view%20of%20busy%20international%20airport%20and%20seaport%20at%20night%2C%20glowing%20lights%2C%20aircraft%20and%20ships%20visible%2C%20dramatic%20bird%20eye%20perspective%2C%20light%20trails%2C%20ultra-realistic%20cinematic%20photography%2C%20deep%20dark%20background%20with%20amber%20and%20white%20lights&width=1600&height=900&seq=srv-hero-bg-001&orientation=landscape)`,
            transform: `translateY(${scrollY * 0.3}px)`,
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-transparent to-black/60" />

        <div className="relative z-10 max-w-5xl mx-auto px-6 text-center py-32 pt-40">
          <div
            className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-widest px-4 py-2 rounded-full border border-white/20 text-white/70 mb-8"
            style={{ background: 'rgba(255,255,255,0.06)' }}
          >
            <i className="ri-global-line" />
            Trusted by 12,000+ businesses worldwide
          </div>

          <h1 className="font-heading font-bold text-5xl md:text-7xl text-white mb-6 leading-tight">
            Logistics Solutions<br />
            <span style={{ color: 'var(--primary)' }}>Built for Scale</span>
          </h1>

          <p className="text-white/60 text-lg md:text-xl max-w-2xl mx-auto leading-relaxed mb-12">
            From a single parcel to a fleet of containers — SwiftTrack delivers across every mode, every market, every hour of the day.
          </p>

          {/* Quick nav pills */}
          <div className="flex flex-wrap justify-center gap-3">
            {SERVICES.map((s) => (
              <a
                key={s.id}
                href={`#${s.id}`}
                className="flex items-center gap-2 px-4 py-2.5 rounded-full text-sm font-medium text-white/75 border border-white/15 hover:border-white/40 hover:text-white transition-all cursor-pointer whitespace-nowrap"
                style={{ background: 'rgba(255,255,255,0.05)' }}
              >
                <i className={`${s.icon} text-sm`} />
                {s.title}
              </a>
            ))}
          </div>
        </div>

        {/* Scroll indicator */}
        <div className="absolute bottom-10 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 text-white/30">
          <span className="text-xs tracking-widest uppercase">Scroll to explore</span>
          <i className="ri-arrow-down-line animate-bounce" />
        </div>
      </div>

      {/* Stats Strip */}
      <div className="bg-gray-950 py-10">
        <div className="max-w-5xl mx-auto px-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {STATS.map(({ value, label }) => (
              <div key={label} className="text-center">
                <div className="font-heading font-bold text-3xl md:text-4xl text-white mb-1">{value}</div>
                <div className="text-gray-500 text-sm">{label}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Services list */}
      <div className="max-w-6xl mx-auto px-6 py-20 space-y-8">
        <div className="text-center mb-16">
          <div className="text-xs font-semibold uppercase tracking-widest mb-3" style={{ color: 'var(--primary)' }}>
            Our Services
          </div>
          <h2 className="font-heading font-bold text-4xl md:text-5xl text-gray-900 mb-4">
            Every mode. Every mile.
          </h2>
          <p className="text-gray-500 text-lg max-w-2xl mx-auto">
            Eight specialised logistics solutions engineered for the demands of modern commerce.
          </p>
        </div>

        {SERVICES.map((service, i) => (
          <ServiceCard key={service.id} service={service} reverse={i % 2 === 1} />
        ))}
      </div>

      {/* Process */}
      <div className="py-20" style={{ background: '#f9fafa' }}>
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-14">
            <div className="text-xs font-semibold uppercase tracking-widest mb-3" style={{ color: 'var(--primary)' }}>
              How It Works
            </div>
            <h2 className="font-heading font-bold text-4xl text-gray-900">Simple from start to finish</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            {[
              { step: '01', icon: 'ri-phone-line', title: 'Book Online or Call', desc: 'Get an instant quote and schedule your shipment in minutes through our platform.' },
              { step: '02', icon: 'ri-box-3-line', title: 'We Collect', desc: 'Our driver picks up your cargo from any address at the time that works for you.' },
              { step: '03', icon: 'ri-radar-line', title: 'Track Live', desc: 'Monitor every stage of your shipment with real-time updates and GPS tracking.' },
              { step: '04', icon: 'ri-checkbox-circle-line', title: 'Delivered Safe', desc: 'Proof of delivery, signature capture and satisfaction guarantee on every shipment.' },
            ].map(({ step, icon, title, desc }) => (
              <div key={step} className="relative">
                <div className="font-heading font-bold text-7xl text-gray-100 absolute -top-4 left-0 select-none leading-none">
                  {step}
                </div>
                <div className="relative pt-6">
                  <div
                    className="w-12 h-12 rounded-2xl flex items-center justify-center mb-5"
                    style={{ background: 'var(--primary-light)' }}
                  >
                    <i className={`${icon} text-xl`} style={{ color: 'var(--primary)' }} />
                  </div>
                  <h3 className="font-heading font-bold text-lg text-gray-900 mb-2">{title}</h3>
                  <p className="text-gray-500 text-sm leading-relaxed">{desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* CTA */}
      <div
        className="py-24 relative overflow-hidden"
        style={{ background: 'linear-gradient(135deg, #071a1a 0%, #0d2b2b 100%)' }}
      >
        <div
          className="absolute inset-0 opacity-15 bg-cover bg-center"
          style={{ backgroundImage: `url(https://readdy.ai/api/search-image?query=aerial%20view%20of%20busy%20freight%20terminal%20with%20planes%20trucks%20and%20containers%20all%20in%20motion%2C%20warm%20amber%20lights%2C%20high%20altitude%20bird%20eye%20view%20at%20dusk%2C%20dynamic%20logistics%20operations%2C%20ultra-realistic%20photography&width=1400&height=600&seq=srv-cta-bg-002&orientation=landscape)` }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-transparent to-black/30" />
        <div className="relative z-10 max-w-3xl mx-auto px-6 text-center">
          <h2 className="font-heading font-bold text-4xl md:text-5xl text-white mb-6 leading-tight">
            Ready to ship smarter?
          </h2>
          <p className="text-white/60 text-lg mb-10 leading-relaxed">
            Get a personalised logistics quote in under 60 seconds. Our specialists are standing by 24/7.
          </p>
          <div className="flex flex-wrap items-center justify-center gap-4">
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 px-8 py-4 rounded-xl text-base font-semibold text-white whitespace-nowrap transition-opacity hover:opacity-90 cursor-pointer"
              style={{ background: 'var(--primary)' }}
            >
              <i className="ri-mail-send-line" />
              Get a Free Quote
            </Link>
            <Link
              to="/track"
              className="inline-flex items-center gap-2 px-8 py-4 rounded-xl text-base font-semibold text-white border border-white/25 hover:bg-white/10 whitespace-nowrap transition-colors cursor-pointer"
            >
              <i className="ri-barcode-box-line" />
              Track a Shipment
            </Link>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}
